import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { supabase } from '../supabase';

/**
 * App-specific auth user: the raw Supabase Auth user enriched with a flat
 * `tenantId` field read from `app_metadata.tenant_id` (NOT `user_metadata`,
 * which is user-editable and unsafe for authorization).
 */
export type AuthUser = User & { tenantId: string | null };

/**
 * Decodes a Supabase access-token JWT payload.
 *
 * WHY THIS EXISTS (owner bug 2026-10-13/14 — "Tenant wird aufgelöst" screen
 * that sign-out/sign-in could not clear):
 *
 * The `tenant_id` / `role` / `legacy_user_id` claims are injected by the
 * Custom Access Token hook (`public.custom_access_token_hook`,
 * supabase/migrations/20260818150000_...). Per Supabase's own docs, that hook
 * "will only modify the access token JWT but NOT the auth response" — so the
 * injected claims exist ONLY in the JWT, never in `session.user.app_metadata`
 * (which mirrors `auth.users.raw_app_meta_data`).
 *
 * Reading `session.user.app_metadata.tenant_id` therefore returns undefined for
 * every user provisioned by the hook rather than by the BEFORE-INSERT trigger
 * (e.g. Google-OAuth users created before the trigger existed). RLS still works
 * because Postgres reads `auth.jwt()`, but the client-side tenant gate in
 * AppRouter saw no tenant and rendered the "Tenant wird aufgelöst" screen.
 *
 * Returns the decoded payload, or null when the token is absent/malformed.
 */
export function decodeJwtPayload(token: string | null | undefined): Record<string, unknown> | null {
    if (!token) return null;
    try {
        const base64Url = token.split('.')[1];
        if (!base64Url) return null;
        let base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        while (base64.length % 4) base64 += '=';
        const json = decodeURIComponent(
            atob(base64)
                .split('')
                .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                .join(''),
        );
        return JSON.parse(json) as Record<string, unknown>;
    } catch {
        return null;
    }
}

/** Return contract of the useAuth hook. */
export interface UseAuthResult {
    user: AuthUser | null;
    session: Session | null;
    loading: boolean;
    isLoaded: boolean;
    isSignedIn: boolean;
    /** Legacy `public.users.id` (app_metadata.legacy_user_id) — matches every FK column. */
    userId: string | null;
    /** Async access-token resolver (Supabase session JWT). */
    getToken: () => Promise<string | null>;
    signOut: () => Promise<void>;
}

/**
 * useAuth — Supabase Auth session wrapper (replaces the previous auth provider's hooks).
 *
 * Returns a Supabase-compatible auth surface so existing call sites only need an import swap:
 *   - `user`        → Supabase Auth user (`app_metadata`, `user_metadata`, `email`, `id`)
 *   - `userId`      → the legacy Supabase `public.users.id` (app_metadata.legacy_user_id)
 *   - `getToken()`  → async access-token resolver (Supabase session JWT)
 *   - `signOut()`   → Supabase sign-out
 *   - `isLoaded`, `isSignedIn`, `loading`
 *
 * `userId` is intentionally `app_metadata.legacy_user_id` (not `user.id`), because
 * that is the value matching every FK column (user_id, created_by, author_id, guest_id)
 * after the auth migration to Supabase. New users get legacy_user_id === their UUID.
 */
export function useAuth(): UseAuthResult {
    const [session, setSession] = useState<Session | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let active = true;

        supabase.auth.getSession().then(({ data: { session: initialSession } }) => {
            if (!active) return;
            setSession(initialSession);
            setLoading(false);
        });

        const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, nextSession) => {
            if (!active) return;
            setSession(nextSession);
            setLoading(false);
        });

        return () => {
            active = false;
            subscription?.unsubscribe();
        };
    }, []);

    const user = useMemo<AuthUser | null>(() => {
        const rawUser = session?.user ?? null;
        if (!rawUser) return null;
        // Tenant claim resolution — JWT FIRST, then app_metadata.
        //
        // The Custom Access Token hook injects tenant_id/role/legacy_user_id
        // into the ACCESS TOKEN only; it never writes them to
        // `session.user.app_metadata` (which mirrors auth.users.raw_app_meta_data
        // and is therefore empty for hook-provisioned users). Reading
        // app_metadata alone produced the "Tenant wird aufgelöst" screen for
        // those users (owner bug 2026-10-13/14). The JWT is the authoritative
        // source — it is the exact same value RLS reads via auth.jwt().
        //
        // app_metadata is kept as a fallback so users provisioned by the
        // BEFORE-INSERT trigger (raw_app_meta_data populated) keep working.
        const jwt = decodeJwtPayload(session?.access_token);
        const jwtAppMeta = (jwt?.app_metadata ?? {}) as Record<string, unknown>;
        const tenantId =
            (jwtAppMeta.tenant_id as string | undefined) ??
            rawUser.app_metadata?.tenant_id ??
            null;
        // Merge the JWT's app_metadata over the raw one so downstream readers
        // (AppAuth.tsx reads app_metadata.role / legacy_user_id) also see the
        // hook-injected claims instead of an empty object.
        return {
            ...rawUser,
            app_metadata: { ...rawUser.app_metadata, ...jwtAppMeta },
            tenantId,
        };
    }, [session?.user, session?.access_token]);

    const isLoaded = !loading;
    const isSignedIn = Boolean(user);
    const userId: string | null = user?.app_metadata?.legacy_user_id ?? user?.id ?? null;

    // IMPORTANT: getToken MUST stay wrapped in useCallback — a new function
    // reference every render causes infinite re-render loops in downstream
    // useEffect deps (React 19 + TanStack Query gotcha).
    const getToken = useCallback(async () => session?.access_token ?? null, [session]);

    const signOut = useCallback(async () => {
        await supabase.auth.signOut();
    }, []);

    return { user, session, loading, isLoaded, isSignedIn, userId, getToken, signOut };
}

export default useAuth;
